<template>
    <div class="p-grid">
        <div class="p-col-12 p-md-12">
            <div class="card p-fluid">

                <div class="flexed-container">
                    <h4 style="width: 200px">RFP Insights</h4>
                    <p>Please note that the collection of metrics for RFP Insights is enabled on all RFPs created on or after April 10th, 2025.  We are currently investigating adding Insights to RFPs created before that date.</p>
                </div>
            </div>
            <div class="card p-fluid">
                <div class="timeframe-selector mb-3">
                    <h5>Select Timeframe</h5>
                    <div class="timeframe-buttons">
                        <div class="button-group">
                            <h6>Previous</h6>
                            <Button :class="{ 'p-button-outlined': selectedTimeframe !== 'prev-week' }"
                                @click="setTimeframe('prev-week')">Week</Button>
                            <Button :class="{ 'p-button-outlined': selectedTimeframe !== 'prev-month' }"
                                @click="setTimeframe('prev-month')">Month</Button>
                            <Button :class="{ 'p-button-outlined': selectedTimeframe !== 'prev-quarter' }"
                                @click="setTimeframe('prev-quarter')">Quarter</Button>
                        </div>
                        <div class="button-group">
                            <h6>Current</h6>
                            <Button :class="{ 'p-button-outlined': selectedTimeframe !== 'current-week' }"
                                @click="setTimeframe('current-week')">Week</Button>
                            <Button :class="{ 'p-button-outlined': selectedTimeframe !== 'current-month' }"
                                @click="setTimeframe('current-month')">Month</Button>
                            <Button :class="{ 'p-button-outlined': selectedTimeframe !== 'current-quarter' }"
                                @click="setTimeframe('current-quarter')">Quarter</Button>
                        </div>
                    </div>
                </div>

                <div class="calender-wrapper">
                    <div class="calendar-container">
                        <Calendar v-model="dates" selectionMode="range" :manualInput="false"
                            :date-format="dd / MM / yyyy" showButtonBar />
                    </div>
                    <Button :disabled="!dates || dates?.length == 0 || dates[0] == null || dates[1] == null"
                        @click="getRfpListInsights" :loading="loading">Get Data</Button>

                </div>

                <div v-if="loading" class="loader">

                    <p>Loading...</p>

                </div>

                <template v-else>
                    <DataTable :value="summaryData" showGridlines class="mb-4" responsiveLayout="scroll">
                        <Column header="Invites Received" field="invitesReceived">
                        
                            <template #body="{ data }">
                                <template v-if="data.invitesReceived > 0">
                                    <a href="#invites-received" @click.prevent="scrollToSection('invites-received')" class="summary-link">{{ data.invitesReceived }}</a>
                                </template>
                                <template v-else>
                                    {{ data.invitesReceived }}
                                </template>
                            </template>

                        </Column>
                        <Column header="RFPs Declined" field="rfpsDeclined">

                            <template #body="{ data }">
                                <template v-if="data.rfpsDeclined > 0">
                                    <a href="#rfps-declined" @click.prevent="scrollToSection('rfps-declined')" class="summary-link">{{ data.rfpsDeclined }}</a>
                                </template>
                                <template v-else>
                                    {{ data.rfpsDeclined }}
                                </template>
                            </template>
                        
                        </Column>
                        <Column header="RFPs Not Responded To" field="rfpsNotResponded">
                            <template #body="{ data }">
                                <template v-if="data.rfpsNotResponded > 0">
                                    <a href="#rfps-not-responded-to" @click.prevent="scrollToSection('rfps-not-responded-to')" class="summary-link">{{ data.rfpsNotResponded }}</a>
                                </template>
                                <template v-else>
                                    {{ data.rfpsNotResponded }}
                                </template>
                            </template>
                        </Column>
                        <Column header="RFPs Participated In" field="rfpsParticipated">
                            <template #body="{ data }">
                                <template v-if="data.rfpsParticipated > 0">
                                    <a href="#rfps-participated-in" @click.prevent="scrollToSection('rfps-participated-in')" class="summary-link">{{ data.rfpsParticipated }}</a>
                                </template>
                                <template v-else>
                                    {{ data.rfpsParticipated }}
                                </template>
                            </template> 
                        </Column>
                        <Column header="RFPs Not Awarded" field="rfpsNotAwarded">
                            <template #body="{ data }">
                                <template v-if="data.rfpsNotAwarded > 0">
                                    <a href="#rfps-not-awarded" @click.prevent="scrollToSection('rfps-not-awarded')" class="summary-link">{{ data.rfpsNotAwarded }}</a> 
                                </template>
                                <template v-else>
                                    {{ data.rfpsNotAwarded }}
                                </template> 
                            </template>
                        </Column>
                        <Column header="RFPs Awarded" field="rfpsAwarded">
                            <template #body="{ data }">
                                <template v-if="data.rfpsAwarded > 0">
                                    <a href="#rfps-awarded" @click.prevent="scrollToSection('rfps-awarded')" class="summary-link">{{ data.rfpsAwarded }}</a>
                                </template>
                                <template v-else>
                                    {{ data.rfpsAwarded }}
                                </template> 
                            </template>
                        </Column>
                        <Column header="Signed Contracts" field="rfpsSigned">
                            <template #body="{ data }">
                                <template v-if="data.rfpsSigned > 0">
                                    <a href="#rfps-signed" @click.prevent="scrollToSection('rfps-signed')" class="summary-link">{{ data.rfpsSigned }}</a>
                                </template>
                                <template v-else>
                                    {{ data.rfpsSigned }}
                                </template>
                            </template>
                        </Column>
                    </DataTable>

                    <template v-for="(items, status) in groupedRfps" :key="status">
                        <div v-if="items.length > 0" class="status-section mb-4" :id="getSectionId(status)">
                            <h5>{{ status }}</h5>
                            <DataTable :value="items" showGridlines :scrollable="true" responsiveLayout="scroll"
                                stripedRows :resizableColumns="true">
                                <template #empty>
                                    <div class="empty-state">
                                        <i class="pi pi-info-circle text-gray-500 text-xl mb-2"></i>
                                        <p class="m-0">No records found for this status</p>
                                    </div>
                                </template>

                                <Column field="id" header="RFP ID" sortable>
                                    <template #body="{ data }">
                                        <div @click="navigateToRfpDetails(data.id)" class="opportunityLink">
                                            {{ data.id }}
                                        </div>
                                    </template>
                                </Column>
                                <Column field="name" header="RFP Title" sortable class="wrap-column"></Column>
                                <Column field="status" header="Status" sortable></Column>
                                <Column field="startDate" header="Start Date" sortable></Column>
                                <Column field="endDate" header="End Date" sortable></Column>
                            </DataTable>
                        </div>
                    </template>
                </template>
            </div>
        </div>
    </div>
</template>

<script>
import { getRole } from '../service/common';
import RfpInsightsService from './../service/RfpInsightsService';

export default {
    data() {
        return {
            products: null,
            dates: null,
            listPayload: null,
            showTelemetry: true,
            summaryData: [],
            groupedRfps: {},
            selectedTimeframe: 'prev-month',
            loading: false,
            userRole: null,
            adminOpportunityKey: null,
            partnerOpportunityKey: null,
            dataAccessCode: null
        }
    },

    productService: null,
    rfpInsightsService: null,
    async created() {
        this.rfpInsightsService = new RfpInsightsService();
        this.setInitialDateRange();
        this.userRole = getRole();
        this.adminOpportunityKey = process.env.VUE_APP_ADMIN_OPPORTUNITY_KEY;
        this.partnerOpportunityKey = process.env.VUE_APP_PARTNER_OPPORTUNITY_KEY;
        this.dataAccessCode = this.userRole === 'channel:business:admin' || this.userRole === 'acx:system:admin' ? this.adminOpportunityKey : this.partnerOpportunityKey;
    },
    methods: {

        getSectionId(status) {
            // Convert status to lowercase and replace spaces and slashes with hyphens
            // First replace multiple spaces with a single space, then convert to lowercase and replace spaces with hyphens
            const id = status.toLowerCase().trim().replace(/\s+/g, '-');
            console.log('Generated ID for status:', status, 'is:', id);
            return id;
        },

        scrollToSection(sectionId) {
            console.log('Scrolling to section:', sectionId);
            const element = document.getElementById(sectionId);
            console.log('Found element:', element);
            if (element) {
                const offset = 80; // Add 100px margin top
                const elementPosition = element.getBoundingClientRect().top;
                const offsetPosition = elementPosition + window.pageYOffset - offset;
                window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth'
                });
            }
        },

        // Navigate to RFP details page
        navigateToRfpDetails(id) {
            if (process.env.VUE_APP_ENV_TYPE == "DEV") {
                window.open(`${process.env.VUE_APP_RFPERFECT_FRONTEND_URL}/details?id=${id}&for=${this.dataAccessCode}`, '_blank');
            } else if (process.env.VUE_APP_ENV_TYPE == "UAT") {
                window.open(`${process.env.VUE_APP_RFPERFECT_FRONTEND_URL}/details?id=${id}&for=${this.dataAccessCode}`, '_blank');
            } else if (process.env.VUE_APP_ENV_TYPE == "PROD") {
                window.open(`${process.env.VUE_APP_RFPERFECT_FRONTEND_URL}/details?id=${id}&for=${this.dataAccessCode}`, '_blank');
            }
        },

        // Set initial date range
        setInitialDateRange() {
            const today = new Date();
            const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

            this.dates = [firstDayOfMonth, today];
            this.getRfpListInsights(); // Call the API with initial date range
        },

        setTimeframe(timeframe) {
            this.selectedTimeframe = timeframe;
            const today = new Date();
            let start = new Date();
            let end = new Date();

            switch (timeframe) {
                case 'prev-week':
                    start = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 7);
                    end = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
                    break;
                case 'prev-month':
                    start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
                    end = new Date(today.getFullYear(), today.getMonth(), 0);
                    break;
                case 'prev-quarter': {
                    const quarter = Math.floor(today.getMonth() / 3);
                    start = new Date(today.getFullYear(), (quarter - 1) * 3, 1);
                    end = new Date(today.getFullYear(), quarter * 3, 0);
                    break;
                }
                case 'current-week': {
                    const day = today.getDay();
                    start = new Date(today.getFullYear(), today.getMonth(), today.getDate() - day);
                    end = new Date(today.getFullYear(), today.getMonth(), today.getDate() + (6 - day));
                    break;
                }
                case 'current-month':
                    start = new Date(today.getFullYear(), today.getMonth(), 1);
                    end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
                    break;
                case 'current-quarter': {
                    const currentQuarter = Math.floor(today.getMonth() / 3);
                    start = new Date(today.getFullYear(), currentQuarter * 3, 1);
                    end = new Date(today.getFullYear(), (currentQuarter + 1) * 3, 0);
                    break;
                }
            }

            this.dates = [start, end];
            this.getRfpListInsights();
        },

        // Get RFP list insights
        async getRfpListInsights() {
            try {
                this.loading = true;
                this.listPayload = {
                    startDate: new Date(this.dates[0].getTime() - this.dates[0].getTimezoneOffset() * 60000).toISOString().split('T')[0] + 'T00:00:00.000Z',
                    endDate: new Date(this.dates[1].getTime() - this.dates[1].getTimezoneOffset() * 60000).toISOString().split('T')[0] + 'T23:59:59.999Z',
                    partnerId: localStorage.getItem("accountId"),
                }
                const res = await this.rfpInsightsService.getRfpListInsights(this.listPayload);

                // Process data for summary table
                const statusCounts = {
                    invitesReceived: 0,
                    rfpsDeclined: 0,
                    rfpsNotResponded: 0,
                    rfpsParticipated: 0,
                    rfpsNotAwarded: 0,
                    rfpsAwarded: 0,
                    rfpsSigned: 0
                };

                // Initialize grouped RFPs
                this.groupedRfps = {
                    'Invites Received': [],
                    'RFPs Declined': [],
                    'RFPs Not Responded To': [],
                    'RFPs Participated In': [],
                    'RFPs Not Awarded': [],
                    'RFPs Awarded': [],
                    'RFPs Signed': []
                };

                const partnerId = localStorage.getItem("accountId");

                // Set total invites received
                statusCounts.invitesReceived = res.data.length;
                this.groupedRfps['Invites Received'] = res.data.map(item => ({
                    id: item.id,
                    name: item.name,
                    status: item.status_display_name,
                    startDate: item.created_at ? new Date(item.created_at).toLocaleDateString() : 'N/A',
                    endDate: item.end_date ? new Date(item.end_date).toLocaleDateString() : 'N/A'
                }));

                // Process each RFP for other categories
                res.data.forEach(item => {
                    const processedItem = {
                        id: item.id,
                        name: item.name,
                        status: item.status_display_name,
                        startDate: item.created_at ? new Date(item.created_at).toLocaleDateString() : 'N/A',
                        endDate: item.end_date ? new Date(item.end_date).toLocaleDateString() : 'N/A'
                    };

                    // RFPs Participated In
                    if ([9, 12, 13, 14, 10].includes(item.vendor_status) &&
                        [2, 5, 6, 3, 16].includes(item.status)) {
                        statusCounts.rfpsParticipated++;
                        this.groupedRfps['RFPs Participated In'].push(processedItem);
                    }
                    // RFPs Declined
                    if (item.vendor_status === 17) {
                        statusCounts.rfpsDeclined++;
                        this.groupedRfps['RFPs Declined'].push(processedItem);
                    }

                    // RFPs Not Awarded
                    if (item.selected_vendor &&
                        item.selected_vendor !== partnerId &&
                        [6, 3].includes(item.status) &&
                        [10, 12].includes(item.vendor_status)) {
                        statusCounts.rfpsNotAwarded++;
                        this.groupedRfps['RFPs Not Awarded'].push(processedItem);
                    }

                    // RFPs Not Responded To
                    if ([5, 6, 3].includes(item.status) &&
                        [7, 8, 9, 13, 14, 11].includes(item.vendor_status)) {
                        statusCounts.rfpsNotResponded++;
                        this.groupedRfps['RFPs Not Responded To'].push(processedItem);
                    }

                    // RFPs In Contracting
                    if ((item.status === 6 || item.status === 3) && item.selected_vendor === partnerId) {
                        statusCounts.rfpsAwarded++;
                        this.groupedRfps['RFPs Awarded'].push(processedItem);
                    }

                    // RFPs Signed
                    if (item.status === 3 && item.selected_vendor === partnerId) {
                        statusCounts.rfpsSigned++;
                        this.groupedRfps['RFPs Signed'].push(processedItem);
                    }
                });

                this.summaryData = [statusCounts];
            } finally {
                this.loading = false;
            }
        },
    },
}
</script>

<style lang="scss" scoped>
.opportunityLink {
    color: darkgreen;
    text-decoration: underline;
    cursor: pointer;
}

.calender-wrapper {
    display: flex;
    margin-top: 10px;
    margin-bottom: 16px;
    gap: 10px;
}

.calendar-container {
    width: 200px;
}

.empty-state {
    display: flex;
    align-items: center;
    gap: 10px;
    justify-content: center;
}

.timeframe-selector {
    .timeframe-buttons {
        display: flex;
        gap: 2rem;

        .button-group {
            display: flex;
            align-items: center;
            gap: 0.5rem;

            h6 {
                margin: 0;
                min-width: 70px;
            }

            button {
                min-width: 100px;
            }
        }
    }
}

.status-section {
    margin-top: 2rem;

    h5 {
        margin-bottom: 1rem;
    }
}

.wrap-column {
    max-width: 300px;
}

.wrap-text {
    white-space: normal;
    word-wrap: break-word;
    word-break: break-word;
}

.p-button:enabled:focus {
    background: #00796b !important;
}

.loader {
    display: flex;
    justify-content: center;
    margin: 2rem auto;
}

.flexed-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
}


// Add these new styles
:deep(.p-datatable) {
    .p-datatable-header {
        background-color: #e4edd9 !important;
        font-size: 1.1rem;
        font-weight: bold;
    }

    .p-datatable-thead > tr > th {
        background-color: #e4edd9 !important;
        color: #333;
        font-size: 1.1rem;
        font-weight: bold;
        padding: 1rem;
        border: 1px solid #ddd;
    }

    .p-datatable-tbody > tr > td {
        padding: 0.75rem 1rem;
        border: 1px solid #ddd;
    }

    // Add subtle hover effect on rows
    .p-datatable-tbody > tr:hover {
        background-color: #f8f9fa;
    }

    // Add subtle shadow to the table
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    border-radius: 4px;
    overflow: hidden;
}

.summary-link {
    color: #00796b;
    text-decoration: underline;
    font-weight: bold;
    cursor: pointer;
    transition: color 0.2s ease;

    &:hover {
        color: #004d40;
        text-decoration: underline;
    }
}

</style>
